/**
 * 视频播放器组件
 * 带分句播放控制
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { SubtitleSegment, VideoBlob } from '../../../db/shadowDb';

/**
 * 将 AudioBuffer 编码为 WAV Blob
 */
function audioBufferToWav(buffer: AudioBuffer): Blob {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const format = 1; // PCM
  const bitDepth = 16;

  const bytesPerSample = bitDepth / 8;
  const blockAlign = numChannels * bytesPerSample;

  const samples = buffer.length;
  const dataSize = samples * blockAlign;
  const bufferSize = 44 + dataSize;

  const arrayBuffer = new ArrayBuffer(bufferSize);
  const view = new DataView(arrayBuffer);

  // WAV header
  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, bufferSize - 8, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, format, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);

  // 交错写入音频数据
  const channelData: Float32Array[] = [];
  for (let ch = 0; ch < numChannels; ch++) {
    channelData.push(buffer.getChannelData(ch));
  }

  let offset = 44;
  for (let i = 0; i < samples; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = Math.max(-1, Math.min(1, channelData[ch][i]));
      const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      view.setInt16(offset, intSample, true);
      offset += 2;
    }
  }

  return new Blob([arrayBuffer], { type: 'audio/wav' });
}

export interface VideoPlayerRef {
  play: () => void;
  pause: () => void;
  seekTo: (timeMs: number) => void;
  getCurrentTime: () => number;
  playSegment: (segment: SubtitleSegment) => void;
  /** 清除片段结束时间（用于整段播放） */
  clearSegmentEnd: () => void;
  /** 提取指定片段的音频 */
  extractSegmentAudio: (segment: SubtitleSegment) => Promise<Blob | null>;
}

interface VideoPlayerProps {
  /** 视频 Blob */
  videoBlob?: VideoBlob;
  /** 当前字幕 */
  currentSegment?: SubtitleSegment;
  /** 时间更新回调 */
  onTimeUpdate?: (timeMs: number) => void;
  /** 播放状态变化 */
  onPlayStateChange?: (playing: boolean) => void;
  /** 片段播放结束 */
  onSegmentEnd?: () => void;
  /** 视频播放完毕（到达结尾） */
  onVideoEnd?: () => void;
}

export const VideoPlayer = forwardRef<VideoPlayerRef, VideoPlayerProps>(
  ({ videoBlob, currentSegment, onTimeUpdate, onPlayStateChange, onSegmentEnd, onVideoEnd }, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const [playing, setPlaying] = useState(false);
    // 片段播放控制：记录开始和结束时间
    const segmentStartTimeRef = useRef<number | null>(null);
    const segmentEndTimeRef = useRef<number | null>(null);
    // 检查定时器
    const checkIntervalRef = useRef<number | null>(null);
    // 回调 ref（避免 stale closure）
    const onSegmentEndRef = useRef(onSegmentEnd);
    onSegmentEndRef.current = onSegmentEnd;
    const onVideoEndRef = useRef(onVideoEnd);
    onVideoEndRef.current = onVideoEnd;

    // 创建视频 URL
    useEffect(() => {
      if (!videoBlob?.blob) {
        setVideoUrl(null);
        return;
      }

      const url = URL.createObjectURL(videoBlob.blob);
      setVideoUrl(url);

      return () => {
        URL.revokeObjectURL(url);
      };
    }, [videoBlob]);

    // 停止检查定时器
    const stopChecking = useCallback(() => {
      if (checkIntervalRef.current !== null) {
        window.clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    }, []);

    // 启动检查定时器
    const startChecking = useCallback(() => {
      stopChecking();
      const video = videoRef.current;
      if (!video) return;

      checkIntervalRef.current = window.setInterval(() => {
        const endTime = segmentEndTimeRef.current;
        if (endTime !== null) {
          const timeMs = video.currentTime * 1000;
          if (timeMs >= endTime) {
            video.pause();
            segmentStartTimeRef.current = null;
            segmentEndTimeRef.current = null;
            stopChecking();
            onSegmentEndRef.current?.();
          }
        }
      }, 50);
    }, [stopChecking]);

    // 保存函数引用供 useImperativeHandle 使用
    const startCheckingRef = useRef(startChecking);
    const stopCheckingRef = useRef(stopChecking);
    startCheckingRef.current = startChecking;
    stopCheckingRef.current = stopChecking;

    // 播放状态事件
    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;

      const handleTimeUpdate = () => {
        const timeMs = video.currentTime * 1000;
        onTimeUpdate?.(timeMs);
      };

      const handlePlay = () => {
        setPlaying(true);
        onPlayStateChange?.(true);
      };

      const handlePause = () => {
        setPlaying(false);
        onPlayStateChange?.(false);
      };

      const handleEnded = () => {
        setPlaying(false);
        onPlayStateChange?.(false);
        onVideoEndRef.current?.();
      };

      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);
      video.addEventListener('ended', handleEnded);

      return () => {
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
        video.removeEventListener('ended', handleEnded);
      };
    }, [onTimeUpdate, onPlayStateChange]);

    // 组件卸载时清理
    useEffect(() => {
      return () => stopChecking();
    }, [stopChecking]);

    // 暴露给父组件的方法
    useImperativeHandle(ref, () => ({
      play: () => {
        videoRef.current?.play().catch((err) => console.warn('Autoplay blocked:', err));
      },
      pause: () => {
        stopCheckingRef.current();
        videoRef.current?.pause();
      },
      seekTo: (timeMs: number) => {
        if (videoRef.current) {
          videoRef.current.currentTime = timeMs / 1000;
        }
      },
      getCurrentTime: () => (videoRef.current?.currentTime || 0) * 1000,
      playSegment: (segment: SubtitleSegment) => {
        if (videoRef.current) {
          // 先停止之前的检查和播放
          stopCheckingRef.current();
          videoRef.current.pause();
          // 设置片段的开始和结束时间
          segmentStartTimeRef.current = segment.startMs;
          segmentEndTimeRef.current = segment.endMs;
          // 跳转到片段开始位置
          videoRef.current.currentTime = segment.startMs / 1000;
          // 启动检查定时器
          startCheckingRef.current();
          // 开始播放
          videoRef.current.play().catch((err) => console.warn('Playback failed:', err));
        }
      },
      /** 清除片段结束时间（用于整段播放） */
      clearSegmentEnd: () => {
        stopCheckingRef.current();
        segmentStartTimeRef.current = null;
        segmentEndTimeRef.current = null;
      },
      /** 提取指定片段的音频 */
      extractSegmentAudio: async (segment: SubtitleSegment): Promise<Blob | null> => {
        if (!videoBlob?.blob) return null;

        try {
          // 使用 Web Audio API 提取音频片段
          const audioContext = new AudioContext();
          const arrayBuffer = await videoBlob.blob.arrayBuffer();
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0));

          // 计算采样范围
          const sampleRate = audioBuffer.sampleRate;
          const startSample = Math.floor((segment.startMs / 1000) * sampleRate);
          const endSample = Math.floor((segment.endMs / 1000) * sampleRate);
          const length = endSample - startSample;

          // 创建新的音频缓冲区
          const newBuffer = audioContext.createBuffer(
            audioBuffer.numberOfChannels,
            length,
            sampleRate
          );

          // 复制数据
          for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
            const sourceData = audioBuffer.getChannelData(channel);
            const targetData = newBuffer.getChannelData(channel);
            for (let i = 0; i < length; i++) {
              targetData[i] = sourceData[startSample + i] || 0;
            }
          }

          // 编码为 WAV
          const wavBlob = audioBufferToWav(newBuffer);
          await audioContext.close();
          return wavBlob;
        } catch (err) {
          console.error('Failed to extract segment audio:', err);
          return null;
        }
      },
    }));

    if (!videoUrl) {
      return (
        <div className="video-container">
          <div className="video-placeholder">视频加载中...</div>
        </div>
      );
    }

    return (
      <div className="video-container">
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          playsInline
          preload="metadata"
        />
      </div>
    );
  }
);

VideoPlayer.displayName = 'VideoPlayer';

// 播放控制按钮组件
interface PlaybackControlsProps {
  onPrev: () => void;
  onPlayCurrent: () => void;
  onNext: () => void;
  onReplay: () => void;
  onListenMode: () => void;
  onToggleSubtitle: () => void;
  isPlaying: boolean;
  hasPrev: boolean;
  hasNext: boolean;
  isListenMode?: boolean;
  showSubtitle?: boolean;
}

export function PlaybackControls({
  onPrev,
  onPlayCurrent,
  onNext,
  onReplay,
  onListenMode,
  onToggleSubtitle,
  isPlaying,
  hasPrev,
  hasNext,
  isListenMode,
  showSubtitle,
}: PlaybackControlsProps) {
  return (
    <div className="playback-controls">
      <button
        type="button"
        className="control-btn"
        onClick={onPrev}
        disabled={!hasPrev || isListenMode}
        title="上一句 (A)"
      >
        ⏮
      </button>
      <button
        type="button"
        className={`control-btn ${isPlaying && !isListenMode ? 'active' : ''}`}
        onClick={onPlayCurrent}
        disabled={isListenMode}
        title="播放/暂停当前句 (S)"
      >
        {isPlaying && !isListenMode ? '⏸' : '▶'}
      </button>
      <button
        type="button"
        className="control-btn"
        onClick={onNext}
        disabled={!hasNext || isListenMode}
        title="下一句 (D)"
      >
        ⏭
      </button>
      <button
        type="button"
        className="control-btn"
        onClick={onReplay}
        disabled={isListenMode}
        title="重播当前句"
      >
        🔄
      </button>
      <button
        type="button"
        className={`control-btn ${!showSubtitle ? 'active' : ''}`}
        onClick={onToggleSubtitle}
        disabled={isListenMode}
        title={showSubtitle ? '关闭字幕' : '显示字幕'}
      >
        {showSubtitle ? '📝' : '🚫'}
      </button>
      <button
        type="button"
        className={`control-btn ${isListenMode ? 'active' : ''}`}
        onClick={onListenMode}
        title={isListenMode ? '退出听读模式' : '整体听读（无字幕）'}
      >
        {isListenMode ? '⏹' : '👂'}
      </button>
    </div>
  );
}
